import { z } from "zod";

export type Role = "client" | "admin" | "psychologist";

export type User = {
  id: string;
  name: string;
  email: string;
  role: Role;
  phone?: string;
};

export type Service = {
  id: string;
  slug: string;
  title: string;
  category: string;
  description: string;
  price: number;
  duration: string;
  assessmentType?: string;
};

export type ContentPost = {
  id: string;
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  body: string;
};

export type BankInfo = {
  name: string;
  bank: string;
  number: string;
};

export type ChildProfile = {
  id: string;
  name: string;
  birthDate?: string;
  schoolLevel?: string;
  notes?: string;
};

export type Payment = {
  id: string;
  status: "pending" | "review" | "verified" | "rejected";
  proofFileName?: string;
  adminNote?: string;
  reviewedAt?: string;
};

export type Order = {
  id: string;
  userId: string;
  serviceId: string;
  childId?: string;
  status: "waiting_payment" | "payment_review" | "confirmed" | "completed" | "cancelled";
  participantName: string;
  notes?: string;
  total: number;
  createdAt: string;
  service?: Service;
  payment?: Payment;
  child?: ChildProfile;
  client?: User;
};

export type Question = {
  id: string;
  prompt: string;
  type: "text" | "textarea" | "single_choice" | "paired_choice";
  options?: string[];
  itemNumber?: number;
  leftText?: string;
  rightText?: string;
  parityNote?: string;
};

export type PapiIdentityField = {
  key: string;
  label: string;
  type: "text" | "date" | "number" | "single_choice";
  required: boolean;
  options?: string[];
};

export type PapiDefinition = {
  durationMinutes: number;
  totalItems: number;
  identityFields: PapiIdentityField[];
  pairs: Array<{ itemNumber: number; leftText: string; rightText: string; parityNote?: string }>;
};

export type PapiRespondentProfile = {
  fullName: string;
  birthDate: string;
  ageYears: number;
  ageMonths: number;
  gender?: string;
  education: string;
  position: string;
  industry: string;
  industryOther?: string;
  tenureMonths: number;
};

export type PapiFactorResult = {
  aspect: string;
  label: string;
  code: string;
  bucket: "upper" | "lower";
  score: number;
  interpretation: string;
};

export type PapiScoringSnapshot = {
  totalAtas: number;
  totalBawah: number;
  factors: PapiFactorResult[];
  aspects: Array<{ aspect: string; factors: PapiFactorResult[] }>;
  validationFlags: string[];
  generatedAt: string;
};

export type AssessmentDraft = {
  respondentProfile?: Partial<PapiRespondentProfile>;
  answers: Array<{ questionId: string; value: "a" | "b" }>;
  updatedAt: string;
};

export type AssessmentReview = {
  reviewerId?: string;
  notes?: string;
  createdAt: string;
};

export type AssessmentTemplate = {
  id: string;
  title: string;
  type: string;
  description: string;
  instructions: string;
  questions?: Question[];
};

export type AssessmentAssignment = {
  id: string;
  status: "assigned" | "submitted" | "reviewed";
  createdAt: string;
  submittedAt?: string;
  dueDate?: string;
  template?: AssessmentTemplate;
  questions?: Question[];
  response?: {
    answers: Array<{ questionId: string; value: string }>;
    respondentProfile?: PapiRespondentProfile;
    scoringSnapshot?: PapiScoringSnapshot;
  };
  draft?: AssessmentDraft;
  review?: AssessmentReview;
  papiDefinition?: PapiDefinition;
  report?: Report;
  client?: User;
  order?: Order;
  child?: ChildProfile;
};

export type Report = {
  id: string;
  title: string;
  fileName: string;
  status: "draft" | "released";
  createdAt: string;
  releasedAt?: string;
  assignmentId?: string;
  orderId?: string;
  client?: User;
};

export type PublicData = {
  services: Service[];
  posts: ContentPost[];
  bank: BankInfo;
};

export type ClientData = {
  publicData: PublicData;
  children: ChildProfile[];
  orders: Order[];
  assignments: AssessmentAssignment[];
  reports: Report[];
};

export type AdminData = {
  summary: Record<string, number>;
  clients: Array<User & { children?: ChildProfile[]; profile?: { city?: string; concerns?: string } }>;
  orders: Order[];
  templates: AssessmentTemplate[];
  submissions: AssessmentAssignment[];
  reports: Report[];
};

export const loginSchema = z.object({
  email: z.string().email("Email tidak valid"),
  password: z.string().min(1, "Kata sandi wajib diisi"),
});

export const registerSchema = z.object({
  name: z.string().min(2, "Nama minimal 2 karakter"),
  email: z.string().email("Email tidak valid"),
  password: z.string().min(8, "Kata sandi minimal 8 karakter"),
  phone: z.string().min(6, "Nomor WhatsApp wajib diisi"),
  city: z.string().optional(),
  concerns: z.string().optional(),
});

export const orderStatusLabel = {
  waiting_payment: "Menunggu pembayaran",
  payment_review: "Bukti ditinjau",
  confirmed: "Terkonfirmasi",
  completed: "Selesai",
  cancelled: "Dibatalkan",
};

export const assignmentStatusLabel = {
  assigned: "Perlu diisi",
  submitted: "Menunggu review",
  reviewed: "Sudah direview",
};

export const paymentStatusLabel = {
  pending: "Menunggu",
  review: "Review admin",
  verified: "Terverifikasi",
  rejected: "Ditolak",
};

export const PAPI_TYPE = "papi_kostick";

export const formatMoney = new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
});

export function isPapiService(service?: Service) {
  return service?.assessmentType === PAPI_TYPE;
}

export function isPapiAssignment(assignment?: AssessmentAssignment | null) {
  return assignment?.template?.type === PAPI_TYPE;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const body = init?.body;
  if (body && !(body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: "Permintaan gagal" }));
    throw new Error(payload.error ?? "Permintaan gagal");
  }

  return response.json() as Promise<T>;
}
